import { call, put, select, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import { getComponentValidationStatus } from '../../../../Components/__helpers__/component.helpers';
import ValidationStatus from '../../../../Data/ValidationStatus';
import {
  exposeQuestionAnsweredEvent,
  requestIdentityUpdateEvent,
} from '../../../../Helpers/events';
import recordQuestionAnswerMutation from './recordQuestionAnswerMutation';
import { convertComponentToSubmissionObject } from '../../../../Helpers/submission';
import {
  ComponentTypes,
  FREE_TEXT_COMPONENTS,
} from '../../../../Data/Components';
import { isOtherSelected } from '../../../../Helpers/conditionality';
import { isDynamicNewsletterOnForm } from '../../../../Components/Page/page.helpers';
import MatchingSupplierCountSource from '../../../../Data/MatchingSupplierCountSource';
import { MATCHING_SUBCATEGORY_WHITELIST } from '../../../../Data/MatchingSuppliers';
import { setMatchingSupplierCountRequestScheduled } from '../../slices/supplierMatches';

const isFreeText = (component) =>
  Boolean(
    FREE_TEXT_COMPONENTS.includes(component?.component?.type) ||
      isOtherSelected(component)
  );

export function* handleAnswerQuestionSideEffectsWorker(action) {
  const { componentId } = action.payload;
  const {
    components,
    optionalFormConfig,
    userIdentities,
    formConfig,
    features,
    supplierMatches,
  } = yield select();
  const { iFrameId } = optionalFormConfig;
  const { countryCode, isDomainInternal } = formConfig;
  const { showBrandMatchCount } = features || {};
  const component = components[`component_${componentId}`];

  if (getComponentValidationStatus({ component }) !== ValidationStatus.VALID) {
    // Exit early, triggering a completed action due to some validation sagas awaiting this to complete.
    yield put({
      type: ActionTypes.HANDLE_ANSWER_QUESTION_SIDE_EFFECTS_COMPLETED,
      payload: { componentId },
    });
    return;
  }

  yield put({
    type: 'EVENTS/setQuestionsWithSideEffectsTriggered',
    payload: {
      componentId,
    },
  });

  exposeQuestionAnsweredEvent({ component, iFrameId, isDomainInternal });
  requestIdentityUpdateEvent(iFrameId);
  const isCheckboxComponent =
    component?.component?.type === ComponentTypes.CHECKBOXES;
  const hasFinalMatchingCount =
    supplierMatches?.matchingSupplierCountSource ===
      MatchingSupplierCountSource.MATCHES ||
    supplierMatches?.matchingSupplierCountSource ===
      MatchingSupplierCountSource.FALLBACK;
  const isEligibleSubcategory = MATCHING_SUBCATEGORY_WHITELIST.includes(
    formConfig?.subcategoryName
  );

  const shouldDeferMatchingSupplierCount = Boolean(
    showBrandMatchCount &&
      isCheckboxComponent &&
      isEligibleSubcategory &&
      !hasFinalMatchingCount
  );

  if (showBrandMatchCount) {
    if (shouldDeferMatchingSupplierCount) {
      yield put(
        setMatchingSupplierCountRequestScheduled({
          isScheduled: true,
          componentId,
        })
      );
    } else {
      yield put({
        type: ActionTypes.REFRESH_MATCHING_SUPPLIER_COUNT,
        payload: { componentId },
      });
      if (supplierMatches?.matchingSupplierCountRequestScheduled) {
        yield put(
          setMatchingSupplierCountRequestScheduled({ isScheduled: false })
        );
      }
    }
  }

  const answerSubmission = convertComponentToSubmissionObject(
    component,
    components
  );

  const hasDynamicNewsletterQuestion = isDynamicNewsletterOnForm(components);
  if (countryCode === 'US' || hasDynamicNewsletterQuestion) {
    try {
      yield call(recordQuestionAnswerMutation, {
        answers: answerSubmission.value,
        componentId,
        isFreeText: isFreeText(component),
        isUnary: answerSubmission.type === 'single',
        lyticsId: userIdentities.lytics,
        questionId: component.component.mappedQuestionId, // Note: Using this to key the values in the gateway may be problematic as we could have multiple with the same answer or empty admiral ids.
        visitorId: userIdentities.visitorId,
        countryCode,
        hasDynamicNewsletterQuestion,
      });
    } catch (error) {
      // TODO: In GOLD-868 We will add appropriate logging when this mutation fails for now do not raise an error in these circumstances
    }
  }
}

/**
 * Redux Watcher
 */
export default function* handleAnswerQuestionSideEffectsWatcher() {
  yield takeEvery(
    ActionTypes.HANDLE_ANSWER_QUESTION_SIDE_EFFECTS,
    handleAnswerQuestionSideEffectsWorker
  );
}
